import { useAllProfilingMaster } from "http-hooks/profilingMaster/useProfilingMaster";
import DeleteProfilingMaster from "components/profilingMaster/DeleteProfilingMaster";
import { useMemo, useState } from "react";
import useModalStore from "store/modals";
import Input from "components/ui/Input";
import useAuth from "hooks/useAuth";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";

const initialFilter = {
  from_date: "",
  to_date: "",
  ps_Id: "",
  page: 1,
  limit: 10,
};

const showDateInWords = (input) => {
  const date = new Date(input);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const columns = [
  {
    key: "partyType",
    header: "Type",
    render: (item) =>
      item.party?.type === "C"
        ? "Customer"
        : item.party?.type === "V"
          ? "Vendor"
          : "-",
  },
  {
    key: "party",
    header: "Party",
    render: (item) => (
      <div className="font-medium text-gray-900">
        {item.party?.name || item.ps_Id}
      </div>
    ),
  },
  {
    key: "date",
    header: "Date",
    render: (item) => showDateInWords(item.date),
  },
  { key: "noofdaysCD", header: "Cash Discount (Days)" },
  { key: "noofdayspay", header: "Payment Days" },
  {
    key: "intprcnt",
    header: "Interest Rate",
    render: (item) => `${item.intprcnt}%`,
  },
  {
    key: "agentcom",
    header: "Agent Commission",
    render: (item) => `${item.agentcom}%`,
    agentOnly: true,
  },
  {
    key: "agencommcond",
    header: "Commission Condition",
    render: (item) =>
      item.agencommcond === "SBT"
        ? "SBT before taxes"
        : item.agencommcond === "SB"
          ? "Sale Bill Amount"
          : "-",
    agentOnly: true,
  },
  { key: "actions", header: "Actions", isActions: true },
];

export default function ProfilingMaster() {
  const { agttp } = useAuth();
  const isTrader = Number(agttp) === 1;
  const listColumns = useMemo(
    () => (isTrader ? columns.filter((col) => !col.agentOnly) : columns),
    [isTrader]
  );
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const { openModal } = useModalStore();

  const openSetModal = (id) => openModal("profiling", id);

  const handleVendorChange = (vendorOption) => {
    setSelectedVendor(vendorOption);
    setSelectedCustomer(null);
    setFilter((prev) => ({
      ...prev,
      ps_Id: vendorOption?.ps_Id || "",
      page: 1,
    }));
  };

  const handleCustomerChange = (customerOption) => {
    setSelectedCustomer(customerOption);
    setSelectedVendor(null);
    setFilter((prev) => ({
      ...prev,
      ps_Id: customerOption?.ps_Id || "",
      page: 1,
    }));
  };

  const handleFromDateChange = (e) => {
    setFilter((prev) => ({ ...prev, from_date: e.target.value, page: 1 }));
  };

  const handleToDateChange = (e) => {
    setFilter((prev) => ({ ...prev, to_date: e.target.value, page: 1 }));
  };

  const clearFilters = () => {
    setSelectedVendor(null);
    setSelectedCustomer(null);
    setFilter(initialFilter);
  };

  const {
    isLoading,
    data: {
      data: allProfilingMasters,
      currentPage,
      totalPage: totalPages,
    } = {},
  } = useAllProfilingMaster(filter);

  return (
    <MasterPage
      title="Profiling Master (Dynamic)"
      description="Manage vendor/customer profiles with dynamic discount configurations."
      headerActions={
        <AddButton onClick={() => openSetModal()}>
          Add Profiling Master
        </AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <Input
                type="date"
                value={filter.from_date}
                onChange={handleFromDateChange}
                label="From Date"
              />
            </FilterCell>
            <FilterCell>
              <Input
                type="date"
                value={filter.to_date}
                onChange={handleToDateChange}
                label="To Date"
              />
            </FilterCell>
            <FilterCell>
              <SelectVendors
                value={selectedVendor}
                onChange={handleVendorChange}
                label="Filter by Vendor"
                placeholder="Select vendor"
              />
            </FilterCell>
            <FilterCell>
              <SelectCustomers
                value={selectedCustomer}
                onChange={handleCustomerChange}
                label="Filter by Customer"
                placeholder="Select customer"
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <ClearFiltersButton onClick={clearFilters} />
          </FilterRow>
        </>
      }
      columns={listColumns}
      data={allProfilingMasters}
      isLoading={isLoading}
      rowKey="id"
      deleteComponent={DeleteProfilingMaster}
      renderActions={(item, setDeleteItemId) => (
        <>
          <EditAction
            onClick={() => openSetModal(item.id)}
            title="Edit Profiling Master"
            variant="secondary"
          />
          <DeleteAction
            onClick={() => setDeleteItemId(item.id)}
            title="Delete Profiling Master"
          />
        </>
      )}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
    />
  );
}
